/**
 * Upload manager: chunked, resumable uploads straight to storage (GCS resumable protocol,
 * or the API's local stand-in). Lives outside React so uploads keep going while people move
 * around Manos Studio, and survives a page refresh: the session is remembered and resumes
 * from the last byte the server confirmed once the same file is chosen again.
 */
import { api, type Media } from "./api";

export type UploadStatus = "queued" | "starting" | "uploading" | "waiting" | "done" | "failed" | "cancelled" | "interrupted";
export type Upload = {
  key: string;
  name: string;
  size: number;
  lastModified: number;
  watermark: boolean;
  status: UploadStatus;
  sent: number;
  speed: number; // bytes per second, smoothed
  mediaId?: string;
  uploadUrl?: string;
  chunkSize?: number;
  error?: string;
};

const STORE_KEY = "manos.uploads.v1";
const CONCURRENCY = 2;
const MAX_ATTEMPTS = 8;

let items: Upload[] = [];
const files = new Map<string, File>();
const xhrs = new Map<string, XMLHttpRequest>();
const listeners = new Set<() => void>();
const mediaListeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());
const mediaChanged = () => mediaListeners.forEach((l) => l());
const update = (key: string, patch: Partial<Upload>) => {
  items = items.map((u) => (u.key === key ? { ...u, ...patch } : u));
  emit();
};
const get = (key: string) => items.find((u) => u.key === key);

/* ---------- Persistence ---------- */
function persist() {
  const resumable = items
    .filter((u) => u.mediaId && u.uploadUrl && !["done", "cancelled", "failed"].includes(u.status))
    .map(({ key, name, size, lastModified, watermark, mediaId, uploadUrl, chunkSize, sent }) => ({ key, name, size, lastModified, watermark, mediaId, uploadUrl, chunkSize, sent }));
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(resumable));
  } catch {
    /* storage full or blocked: uploads still work, they just can't resume after a refresh */
  }
}
function restore() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY) ?? "[]") as Omit<Upload, "status" | "speed">[];
    items = saved.map((u) => ({ ...u, status: "interrupted" as const, speed: 0 }));
  } catch {
    items = [];
  }
}
restore();

/* ---------- Public API ---------- */
export const uploads = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => void listeners.delete(listener);
  },
  snapshot: () => items,
  /** Called whenever an upload creates or finishes a media record (to refresh the library). */
  onMediaChanged(listener: () => void) {
    mediaListeners.add(listener);
    return () => void mediaListeners.delete(listener);
  },

  add(list: FileList | File[], opts: { watermark: boolean }) {
    for (const file of Array.from(list)) {
      if (!file.size) continue;
      const key = crypto.randomUUID();
      files.set(key, file);
      items = [...items, { key, name: file.name, size: file.size, lastModified: file.lastModified, watermark: opts.watermark, status: "queued", sent: 0, speed: 0 }];
    }
    emit();
    pump();
  },

  /** Continue an upload interrupted by a refresh or closed tab. The file must be the same one. */
  resume(key: string, file: File) {
    const u = get(key);
    if (!u) return "That upload is no longer listed.";
    if (file.name !== u.name || file.size !== u.size) return `That's a different file. Choose "${u.name}" to continue.`;
    files.set(key, file);
    update(key, { status: "queued", error: undefined });
    pump();
    return null;
  },

  async cancel(key: string) {
    const u = get(key);
    if (!u) return;
    xhrs.get(key)?.abort();
    update(key, { status: "cancelled" });
    files.delete(key);
    persist();
    if (u.mediaId) await api(`/studio/media/${u.mediaId}`, { method: "DELETE" }).catch(() => {});
    mediaChanged();
  },

  retry(key: string) {
    if (!files.has(key)) return;
    update(key, { status: "queued", error: undefined });
    pump();
  },

  dismiss(key: string) {
    items = items.filter((u) => u.key !== key);
    files.delete(key);
    persist();
    emit();
  },

  clearFinished() {
    items = items.filter((u) => !["done", "cancelled"].includes(u.status));
    emit();
  },

  /** Open the system file chooser and upload what's picked. */
  pick() {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "video/*,image/*,.mov,.mxf,.mkv,.tif,.tiff,.heic";
    input.onchange = () => input.files?.length && uploads.add(input.files, { watermark: watermarkDefault.get() });
    input.click();
  },

  active: () => items.some((u) => ["queued", "starting", "uploading", "waiting"].includes(u.status)),
  forMedia: (mediaId: string) => items.find((u) => u.mediaId === mediaId && u.status !== "done"),
};

/* ---------- Engine ---------- */
const running = () => items.filter((u) => ["starting", "uploading", "waiting"].includes(u.status)).length;

function pump() {
  for (const u of items) {
    if (running() >= CONCURRENCY) break;
    if (u.status === "queued" && files.has(u.key)) void run(u.key);
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const online = () =>
  navigator.onLine ? Promise.resolve() : new Promise<void>((r) => window.addEventListener("online", () => r(), { once: true }));

type PutResult = { done: boolean; next: number };

/** One PUT to the resumable session. 308 = keep going (Range says what arrived); 200/201 = complete. */
function put(key: string, url: string, body: Blob | null, range: string, onProgress?: (loaded: number) => void) {
  return new Promise<PutResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhrs.set(key, xhr);
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Range", range);
    if (onProgress) xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      xhrs.delete(key);
      if (xhr.status === 200 || xhr.status === 201) return resolve({ done: true, next: 0 });
      if (xhr.status === 308) {
        const m = /bytes=0-(\d+)/.exec(xhr.getResponseHeader("Range") ?? "");
        return resolve({ done: false, next: m ? Number(m[1]) + 1 : 0 });
      }
      const err = new Error(xhr.status === 403 || xhr.status === 404 ? "expired" : `HTTP ${xhr.status}`);
      (err as Error & { fatal?: boolean }).fatal = xhr.status === 403 || xhr.status === 404 || xhr.status === 400;
      reject(err);
    };
    xhr.onerror = () => {
      xhrs.delete(key);
      reject(new Error("network"));
    };
    xhr.onabort = () => {
      xhrs.delete(key);
      reject(new Error("aborted"));
    };
    xhr.send(body);
  });
}

async function run(key: string) {
  const file = files.get(key);
  let u = get(key);
  if (!file || !u) return;
  // Cancel can land during any await below; check after each one and never overwrite "cancelled".
  const cancelled = () => get(key)?.status === "cancelled";
  try {
    if (!u.mediaId) {
      update(key, { status: "starting" });
      const res = await api<{ media: Media; uploadUrl: string; chunkSize: number }>("/studio/media/uploads", {
        method: "POST",
        json: { filename: file.name, contentType: file.type || "application/octet-stream", size: file.size, watermark: u.watermark },
      });
      if (cancelled()) {
        // Cancelled before the record existed, so cancel() couldn't delete it: do it now.
        await api(`/studio/media/${res.media.id}`, { method: "DELETE" }).catch(() => {});
        mediaChanged();
        return;
      }
      update(key, { mediaId: res.media.id, uploadUrl: res.uploadUrl, chunkSize: res.chunkSize });
      persist();
      mediaChanged();
    }
    u = get(key)!;
    const url = u.uploadUrl!, size = file.size, chunk = u.chunkSize ?? 16 * 1024 * 1024;
    update(key, { status: "uploading" });

    // Ask the server what it already has (matters after a refresh or a dropped connection).
    let offset = 0, attempts = 0;
    const status = await put(key, url, null, `bytes */${size}`);
    if (cancelled()) return;
    if (status.done) offset = size;
    else offset = status.next;

    let lastTime = performance.now(), lastBytes = offset;
    while (offset < size) {
      if (get(key)?.status === "cancelled") return;
      const end = Math.min(offset + chunk, size);
      try {
        const r = await put(key, url, file.slice(offset, end), `bytes ${offset}-${end - 1}/${size}`, (loaded) => {
          const sent = offset + loaded, now = performance.now();
          if (now - lastTime > 500) {
            const instant = ((sent - lastBytes) / (now - lastTime)) * 1000;
            const prev = get(key)?.speed ?? 0;
            update(key, { sent, speed: prev ? prev * 0.7 + instant * 0.3 : instant });
            lastTime = now;
            lastBytes = sent;
          }
        });
        attempts = 0;
        offset = r.done ? size : r.next;
        if (cancelled()) return;
        update(key, { sent: offset, status: "uploading" });
      } catch (err) {
        const e = err as Error & { fatal?: boolean };
        if (e.message === "aborted") return;
        if (e.fatal) throw new Error(e.message === "expired" ? "The upload link expired. Remove this and upload the file again." : "The server rejected this upload.");
        if (++attempts > MAX_ATTEMPTS) throw new Error("The connection kept dropping. Check your network, then retry.");
        update(key, { status: "waiting", speed: 0 });
        await online();
        await sleep(Math.min(30_000, 1000 * 2 ** (attempts - 1)));
        if (cancelled()) return;
        const s = await put(key, url, null, `bytes */${size}`).catch(() => null);
        if (cancelled()) return;
        if (s) offset = s.done ? size : s.next;
        update(key, { status: "uploading", sent: offset });
      }
    }
    update(key, { status: "done", sent: size, speed: 0 });
    files.delete(key);
    persist();
    mediaChanged();
  } catch (err) {
    // An abort from cancel() is a quiet stop, not a failure.
    if (cancelled() || (err instanceof Error && err.message === "aborted")) return;
    const message = err instanceof Error ? err.message : "Upload failed";
    update(key, { status: "failed", error: message, speed: 0 });
    persist();
  } finally {
    pump();
  }
}

/** After a refresh, ask the server how much of each paused upload already arrived, so the tray shows real progress. */
async function refreshPaused() {
  for (const u of items.filter((x) => x.status === "interrupted" && x.uploadUrl)) {
    const r = await put(u.key, u.uploadUrl!, null, `bytes */${u.size}`).catch(() => null);
    if (r) update(u.key, { sent: r.done ? u.size : r.next });
  }
}
void refreshPaused();

// Leaving mid-upload is safe (it resumes), but warn so nobody closes the tab by accident.
window.addEventListener("beforeunload", (e) => {
  if (uploads.active()) e.preventDefault();
});

/* ---------- Watermark preference for new uploads ---------- */
const WM_KEY = "manos.watermarkNew";
export const watermarkDefault = {
  get: () => {
    try {
      return localStorage.getItem(WM_KEY) === "1";
    } catch {
      return false;
    }
  },
  set: (on: boolean) => {
    try {
      localStorage.setItem(WM_KEY, on ? "1" : "0");
    } catch {
      /* ignore */
    }
  },
};
